import { useState } from 'react';
import { Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';

import CheckCircleFilledIcon from 'modules/common/components/Icons/CheckCircleFilledIcon';
import BankLogo from 'modules/HubV2/Bookkeeping/components/Banks2/BankList/Bank/BankLogo';
import AutoStatementsEligibleModal from 'modules/common/components/StatementUpload/AutoStatementsEligibleModal';
import AutoStatementsSuccessModal from 'modules/Settings/BankAccountsCombined/components/AutoStatementsSuccessModal';
import useQuilttConnect from 'modules/common/components/StatementUpload/hooks/useQuilttConnect';
import { StatementUploadAccount } from 'modules/common/components/StatementUpload/StatementUpload.interface';
import { TaskResolvability } from 'modules/common/components/StatementUpload/hooks/useStatementUploadResolvability';
import usePersistData from 'modules/common/hooks/usePersistData';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import STATEMENT_UPLOAD_ANALYTICS_EVENTS from 'services/statementUploadTasks/constants/analytics';

interface StatementUploadGroupRowProps {
    readonly institutionName: string;
    readonly accounts: StatementUploadAccount[];
    readonly plaidInstitutionId?: string;
    readonly reason?: string;
    readonly resolvability: TaskResolvability;
    readonly statementsNeededCount: number;
    readonly isCompleted: boolean;
    readonly taskId: string;
    readonly year: number;
    readonly month: number;
    readonly onUploadClick: () => void;
    readonly onReconnectSuccess: () => void;
}

/** All accounts have Quiltt auto-statements enabled (is_quiltt AND a quiltt_account_id). */
function allAccountsAutoEnabled(accounts: StatementUploadAccount[]): boolean {
    return accounts.length > 0 && accounts.every((a) => a.is_quiltt && !!a.quiltt_account_id);
}

function getModalStorageKey(institutionName: string): string {
    return `auto_statements_modal_seen_${institutionName}`;
}

function ReconnectButton({
    connectorId,
    connectionId,
    institutionName,
    onSuccess,
}: Readonly<{
    connectorId: string;
    connectionId?: string | null;
    institutionName: string;
    onSuccess?: () => void;
}>) {
    const { open, isConnecting } = useQuilttConnect({
        connectorId,
        connectionId,
        institutionName,
        sourceSurface: 'statement_upload_task_card_reconnect_button',
        onSuccess,
    });

    return (
        <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={open}
            disabled={isConnecting}
            sx={{ minWidth: 100 }}>
            {isConnecting ? <CircularProgress size={20} color="inherit" /> : 'Reconnect'}
        </Button>
    );
}

/**
 * A single institution row in the monthly-statement group side panel: bank logo, name + status, and
 * the primary action (Upload / Reconnect / auto-enabled check). Mirrors the legacy InstitutionRow's
 * eligible-promo / reconnect / auto-enable behavior, restyled as a list row per the Figma side panel.
 */
export default function StatementUploadGroupRow({
    institutionName,
    accounts,
    plaidInstitutionId,
    reason,
    resolvability,
    statementsNeededCount,
    isCompleted,
    taskId,
    year,
    month,
    onUploadClick,
    onReconnectSuccess,
}: Readonly<StatementUploadGroupRowProps>) {
    const theme = useTheme();
    const { sendTrackingEvent } = useEventTracking();
    const { getData, setData } = usePersistData<{ year: number; month: number } | null>();
    const isAutoEnabled = allAccountsAutoEnabled(accounts);
    const [showEligibleModal, setShowEligibleModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Quiltt hook lives here (above the promo modal) so its callbacks stay registered after we close
    // the promo modal to avoid stacked modals. Success is deferred to success-modal dismissal.
    const { open: openQuilttConnector, isConnecting } = useQuilttConnect({
        connectorId: resolvability.connectorId ?? '',
        institutionName,
        sourceSurface: 'statement_upload_task_card_eligible_promo_modal',
        onSuccess: () => setShowSuccessModal(true),
    });

    const hasSeenAutoStatementsModal = (): boolean => {
        const stored = getData(getModalStorageKey(institutionName));
        return stored?.year === year && stored?.month === month;
    };

    const isEligibleForAutoStatements =
        !isCompleted &&
        !isAutoEnabled &&
        !!resolvability.connectorId &&
        !resolvability.isConnectionLost &&
        !reason &&
        resolvability.isResolvable;

    // A broken connection at a bank we no longer offer Auto Statements for reads as an ordinary upload row:
    // the Upload button and the statements-needed count.
    const asksToReconnect = resolvability.isConnectionLost && resolvability.offerAutoStatements;

    const isConnectionLost = !isCompleted && !isAutoEnabled && !!resolvability.connectorId && asksToReconnect;

    const allStatementsUploaded = !isAutoEnabled && statementsNeededCount === 0;

    const handleOpenUploadModal = () => {
        const showsEligibleModal = isEligibleForAutoStatements && !hasSeenAutoStatementsModal();
        sendTrackingEvent(STATEMENT_UPLOAD_ANALYTICS_EVENTS.TASK_CARD_UPLOAD_CLICKED, {
            institution_name: institutionName,
            task_id: taskId,
            year,
            month,
            destination: showsEligibleModal ? 'eligible_modal' : 'upload_modal',
        });
        if (showsEligibleModal) {
            setData(getModalStorageKey(institutionName), { year, month });
            setShowEligibleModal(true);
        } else {
            onUploadClick();
        }
    };

    const handleModalEnable = () => {
        // Close the promo modal before opening Quiltt to avoid stacked modals.
        setShowEligibleModal(false);
        openQuilttConnector();
    };

    const handleModalDismiss = (method: 'upload_button' | 'close_modal') => {
        sendTrackingEvent(STATEMENT_UPLOAD_ANALYTICS_EVENTS.AUTO_STATEMENTS_MODAL_DISMISSED, {
            institution_name: institutionName,
            source_surface: method,
        });
        setShowEligibleModal(false);
        onUploadClick();
    };

    // A completed task keeps its row in the panel — the CTA is replaced by a check so the member sees
    // confirmation instead of the row disappearing. Auto Statements enabled reads the same way.
    const showCheck = isAutoEnabled || isCompleted;

    const status: { label: string; color: string } = (() => {
        if (isAutoEnabled) {
            return { label: 'Auto Statements enabled', color: 'success.main' };
        }
        if (isCompleted) {
            return { label: 'Statements uploaded', color: 'success.main' };
        }
        if (asksToReconnect) {
            return { label: 'Connection lost', color: 'error.main' };
        }
        if (reason) {
            return { label: reason, color: 'warning.main' };
        }
        if (statementsNeededCount > 0) {
            const noun = statementsNeededCount === 1 ? 'statement' : 'statements';
            return { label: `${statementsNeededCount} ${noun} needed`, color: 'text.secondary' };
        }
        return { label: 'Statements uploaded', color: 'success.main' };
    })();

    return (
        <>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 2, px: 2, py: 2 }}>
                <BankLogo institutionId={plaidInstitutionId} width={40} height={40} />
                <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
                    <Typography variant="subtitle1" noWrap>
                        {institutionName}
                    </Typography>
                    <Typography variant="body2" color={status.color}>
                        {status.label}
                    </Typography>
                </Stack>
                <Stack direction="row" sx={{ alignItems: 'center', flexShrink: 0 }}>
                    {showCheck && <CheckCircleFilledIcon fill={theme.palette.success.main} />}
                    {!showCheck && isConnectionLost && (
                        <ReconnectButton
                            connectorId={resolvability.connectorId!}
                            connectionId={resolvability.connectionId}
                            institutionName={institutionName}
                            onSuccess={onReconnectSuccess}
                        />
                    )}
                    {!showCheck && !isConnectionLost && (
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={handleOpenUploadModal}
                            sx={{ minWidth: 100 }}>
                            {allStatementsUploaded ? 'Review' : 'Upload'}
                        </Button>
                    )}
                </Stack>
            </Stack>

            <AutoStatementsEligibleModal
                open={showEligibleModal}
                onDismiss={handleModalDismiss}
                onEnable={handleModalEnable}
                institutionName={institutionName}
                isConnecting={isConnecting}
            />
            <AutoStatementsSuccessModal
                open={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    onReconnectSuccess();
                }}
                institutionName={institutionName}
            />
        </>
    );
}
